import {
  commitBoundedDecimalDraft,
  sanitizeBoundedDecimalDraft,
} from '../utils/numberInputDraft';

export interface ReactionWeightSide {
  like?: number;
  dislike?: number;
  propagation?: number;
}

export interface ReactionWeights {
  admin?: ReactionWeightSide;
  user?: ReactionWeightSide;
}

export interface ReactionWeightsFormState {
  admin_like: string;
  admin_dislike: string;
  admin_propagation: string;
  user_like: string;
  user_dislike: string;
  user_propagation: string;
}

export const EMPTY_REACTION_WEIGHTS_FORM: ReactionWeightsFormState = {
  admin_like: '',
  admin_dislike: '',
  admin_propagation: '',
  user_like: '',
  user_dislike: '',
  user_propagation: '',
};

/** Default weights for dataset import (all signals weighted equally). */
export const DEFAULT_REACTION_WEIGHTS_FORM: ReactionWeightsFormState = {
  admin_like: '1',
  admin_dislike: '1',
  admin_propagation: '1',
  user_like: '1',
  user_dislike: '1',
  user_propagation: '1',
};

const REACTION_WEIGHT_FIELD_LABELS: Record<keyof ReactionWeightsFormState, string> = {
  admin_like: 'Admin like weight',
  admin_dislike: 'Admin dislike weight',
  admin_propagation: 'Admin propagation to internal agents',
  user_like: 'User like weight',
  user_dislike: 'User dislike weight',
  user_propagation: 'User propagation to internal agents',
};

export const REACTION_WEIGHT_MIN = 0;
export const REACTION_WEIGHT_MAX = 1;
export const REACTION_WEIGHT_STEP = 0.1;

/** Keep reaction-weight draft in [0, 1] while the user types. */
export function sanitizeReactionWeightDraft(raw: string): string {
  return sanitizeBoundedDecimalDraft(raw, REACTION_WEIGHT_MIN, REACTION_WEIGHT_MAX);
}

/** Clamp and snap to step on blur. */
export function commitReactionWeightDraft(raw: string): string {
  return commitBoundedDecimalDraft(raw, REACTION_WEIGHT_MIN, REACTION_WEIGHT_MAX, REACTION_WEIGHT_STEP);
}

function parseReactionWeightInput(value: string): number | undefined {
  const trimmed = value.trim();
  if (trimmed === '') return undefined;
  const num = Number(trimmed);
  if (!Number.isFinite(num)) return undefined;
  return Math.min(REACTION_WEIGHT_MAX, Math.max(REACTION_WEIGHT_MIN, num));
}

export interface ReactionWeightsValidationError {
  field: keyof ReactionWeightsFormState;
  message: string;
}

export type ReactionWeightsFieldErrors = Partial<Record<keyof ReactionWeightsFormState, string>>;

export function validateReactionWeightsForm(form: ReactionWeightsFormState): ReactionWeightsFieldErrors {
  const errors: ReactionWeightsFieldErrors = {};
  for (const [key, label] of Object.entries(REACTION_WEIGHT_FIELD_LABELS) as Array<
    [keyof ReactionWeightsFormState, string]
  >) {
    const raw = form[key].trim();
    if (raw === '') continue;
    const num = Number(raw);
    if (!Number.isFinite(num)) {
      errors[key] = `${label} must be a number between 0 and 1.`;
      continue;
    }
    if (num < 0 || num > 1) {
      errors[key] = `${label} must be between 0 and 1.`;
    }
  }
  return errors;
}

export function reactionWeightsValidationSummary(errors: ReactionWeightsFieldErrors): string | null {
  const entries = Object.entries(errors);
  if (entries.length === 0) return null;
  if (entries.length === 1) return entries[0][1];
  return `Fix ${entries.length} reaction weight fields — values must be between 0 and 1.`;
}

export function firstReactionWeightsValidationField(
  errors: ReactionWeightsFieldErrors
): keyof ReactionWeightsFormState | null {
  const field = Object.keys(errors)[0];
  return field ? (field as keyof ReactionWeightsFormState) : null;
}

export function buildReactionWeightsFromForm(form: ReactionWeightsFormState): ReactionWeights | undefined {
  const adminEntries = [
    ['like', parseReactionWeightInput(form.admin_like)],
    ['dislike', parseReactionWeightInput(form.admin_dislike)],
    ['propagation', parseReactionWeightInput(form.admin_propagation)],
  ].filter((entry): entry is [string, number] => entry[1] !== undefined);

  const userEntries = [
    ['like', parseReactionWeightInput(form.user_like)],
    ['dislike', parseReactionWeightInput(form.user_dislike)],
    ['propagation', parseReactionWeightInput(form.user_propagation)],
  ].filter((entry): entry is [string, number] => entry[1] !== undefined);

  if (adminEntries.length === 0 && userEntries.length === 0) {
    return undefined;
  }

  return {
    ...(adminEntries.length > 0 ? { admin: Object.fromEntries(adminEntries) } : {}),
    ...(userEntries.length > 0 ? { user: Object.fromEntries(userEntries) } : {}),
  };
}
